import { getSupabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import type {
  HackathonWednesdayPulseRow,
  HackathonWeekAssignmentRow,
  HackathonWeekResultRow,
  McUserForPmAdmin,
  PersonalManagerDayRow,
  PersonalManagerPartnerDayStatus,
  PersonalManagerListItem,
  PersonalManagerPartner,
  PersonalManagerPartnerCoverageRow,
  PersonalManagerRoyaltyStatusRow,
  PersonalManagerTaskCode,
  PersonalManagerTaskStatus,
} from '@/types'

function formatError(error: unknown): string {
  if (!error) return 'Unknown error'
  if (error instanceof Error) return error.message
  if (typeof error === 'object') {
    const e = error as { message?: string; details?: string; hint?: string; code?: string }
    return [e.message, e.details, e.hint, e.code].filter(Boolean).join(' | ') || JSON.stringify(error)
  }
  return String(error)
}

export async function listPersonalManagers(): Promise<PersonalManagerListItem[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_personal_managers')
  )
  if (error) throw new Error(formatError(error))
  // `royalty_offset_rub` появляется миграцией 20260805250000, `royalty_rb_tax_offset_rub` —
  // 20261013150000: нормализуем, чтобы экран не показывал NaN на проде до их применения.
  return ((data ?? []) as any[]).map((row) => ({
    ...row,
    royalty_offset_rub: Number(row.royalty_offset_rub ?? 0),
    royalty_rb_tax_offset_rub: Number(row.royalty_rb_tax_offset_rub ?? 0),
  })) as PersonalManagerListItem[]
}

export async function listMcUsersForPmAdmin(): Promise<McUserForPmAdmin[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_mc_users_for_pm_admin')
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as McUserForPmAdmin[]
}

export async function setUserPersonalManager(params: {
  userId: string
  isPm: boolean
  note?: string | null
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('set_user_personal_manager', {
      p_user_id: params.userId,
      p_is_pm: params.isPm,
      p_note: params.note ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function getPersonalManagerPartners(managerUserId: string): Promise<PersonalManagerPartner[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_personal_manager_partners', {
      p_manager_user_id: managerUserId,
    })
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as PersonalManagerPartner[]
}

export async function getPersonalManagerRoyaltyStatus(managerUserId: string): Promise<PersonalManagerRoyaltyStatusRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_personal_manager_royalty_status', {
      p_manager_user_id: managerUserId,
    })
  )
  if (error) throw new Error(formatError(error))
  return ((data ?? []) as any[]).map((row) => ({
    ...row,
    partner_number: Number(row.partner_number),
    charged_rub: Number(row.charged_rub ?? 0),
    paid_rub: Number(row.paid_rub ?? 0),
    written_off_rub: Number(row.written_off_rub ?? 0),
    offset_rub: Number(row.offset_rub ?? 0),
    debt_rub: Number(row.debt_rub ?? 0),
    credit_rub: Number(row.credit_rub ?? 0),
    requests_count: Number(row.requests_count ?? 0),
    requests_sent_count: Number(row.requests_sent_count ?? 0),
  })) as PersonalManagerRoyaltyStatusRow[]
}

export async function listPersonalManagerPartnerCoverage(): Promise<PersonalManagerPartnerCoverageRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_personal_manager_partner_coverage')
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as PersonalManagerPartnerCoverageRow[]
}

export async function assignPersonalManager(params: {
  managerUserId: string
  partnerNumber: number
  assignedFrom?: string | null
  note?: string | null
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('assign_personal_manager', {
      p_manager_user_id: params.managerUserId,
      p_partner_number: params.partnerNumber,
      p_assigned_from: params.assignedFrom ?? null,
      p_note: params.note ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
  return data as number
}

export async function transferPersonalManager(params: {
  partnerNumber: number
  newManagerUserId: string
  effectiveFrom?: string | null
  note?: string | null
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('transfer_personal_manager', {
      p_partner_number: params.partnerNumber,
      p_new_manager_user_id: params.newManagerUserId,
      p_effective_from: params.effectiveFrom ?? null,
      p_note: params.note ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
  return data as number
}

export async function updatePersonalManagerAssignmentStart(params: {
  assignmentId: number
  assignedFrom: string
  note?: string | null
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('update_personal_manager_assignment_start', {
      p_assignment_id: params.assignmentId,
      p_assigned_from: params.assignedFrom,
      p_note: params.note ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function unassignPersonalManager(params: {
  assignmentId: number
  unassignedAt?: string | null
  note?: string | null
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('unassign_personal_manager', {
      p_assignment_id: params.assignmentId,
      p_unassigned_at: params.unassignedAt ?? null,
      p_note: params.note ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function getPersonalManagerDayRows(
  managerUserId: string,
  taskDate?: string | null
): Promise<PersonalManagerDayRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_personal_manager_day_rows', {
      p_manager_user_id: managerUserId,
      p_task_date: taskDate ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as PersonalManagerDayRow[]
}

export async function setPersonalManagerTaskMark(params: {
  taskDate: string
  studioInternalId: number
  taskCode: PersonalManagerTaskCode
  status: PersonalManagerTaskStatus
  note?: string | null
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('set_personal_manager_task_mark', {
      p_task_date: params.taskDate,
      p_studio_internal_id: params.studioInternalId,
      p_task_code: params.taskCode,
      p_status: params.status,
      p_note: params.note ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function setPersonalManagerDayOff(params: {
  managerUserId: string
  taskDate: string
  isDayOff: boolean
  note?: string | null
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('set_personal_manager_day_off', {
      p_manager_user_id: params.managerUserId,
      p_task_date: params.taskDate,
      p_is_day_off: params.isDayOff,
      p_note: params.note ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function setPersonalManagerPartnerDayOverride(params: {
  taskDate: string
  partnerNumber: number
  overrideType: PersonalManagerPartnerDayStatus | null
  note?: string | null
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('set_personal_manager_partner_day_override', {
      p_task_date: params.taskDate,
      p_partner_number: params.partnerNumber,
      p_override_type: params.overrideType,
      p_note: params.note ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
}

function mapHackathonAssignmentRow(row: Record<string, unknown>): HackathonWeekAssignmentRow {
  return {
    week_id: Number(row.week_id),
    week_start: String(row.week_start ?? ''),
    week_end: String(row.week_end ?? ''),
    partner_number: Number(row.partner_number),
    persons_fio: String(row.persons_fio ?? `Франчайзи #${String(row.partner_number ?? '')}`),
    manager_user_id: row.manager_user_id ? String(row.manager_user_id) : null,
    manager_name: row.manager_name ? String(row.manager_name) : null,
    segment: row.segment === 'subscription' ? 'subscription' : 'regular',
    open_studio_count: Number(row.open_studio_count ?? 0),
    avg_weekly_revenue_rub: Number(row.avg_weekly_revenue_rub ?? 0),
    avg_weekly_revenue_per_studio_rub: Number(row.avg_weekly_revenue_per_studio_rub ?? 0),
    has_subscription_or_mixed: Boolean(row.has_subscription_or_mixed),
    suggested_group_number: Number(row.suggested_group_number ?? 0),
    is_participating: Boolean(row.is_participating ?? true),
    group_number: row.group_number == null ? null : Number(row.group_number),
    assignment_source: row.assignment_source === 'manual' ? 'manual' : 'auto',
    has_saved_assignment: Boolean(row.has_saved_assignment),
    carried_from_week_start: row.carried_from_week_start ? String(row.carried_from_week_start) : null,
    carried_from_week_end: row.carried_from_week_end ? String(row.carried_from_week_end) : null,
  }
}

export async function listHackathonWeekAssignments(weekId: number): Promise<HackathonWeekAssignmentRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_hackathon_week_assignments', {
      p_week_id: weekId,
      p_manager_user_id: null,
    })
  )
  if (error) throw new Error(formatError(error))
  return ((data ?? []) as Array<Record<string, unknown>>).map(mapHackathonAssignmentRow)
}

export async function saveHackathonWeekAssignments(params: {
  weekId: number
  rows: Array<{
    partner_number: number
    segment: 'regular' | 'subscription'
    is_participating: boolean
    group_number: number | null
    assignment_source: 'auto' | 'manual'
  }>
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('save_hackathon_week_assignments', {
      p_week_id: params.weekId,
      p_rows: params.rows,
    })
  )
  if (error) throw new Error(formatError(error))
  return Number(data ?? 0)
}

export async function listHackathonWeekResults(weekId: number): Promise<HackathonWeekResultRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_hackathon_week_results', {
      p_week_id: weekId,
      p_manager_user_id: null,
    })
  )
  if (error) throw new Error(formatError(error))
  return ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
    week_id: Number(row.week_id),
    report_date: String(row.report_date ?? ''),
    segment: row.segment === 'subscription' ? 'subscription' : 'regular',
    group_number: Number(row.group_number ?? 0),
    partner_number: Number(row.partner_number),
    persons_fio: String(row.persons_fio ?? `Франчайзи #${String(row.partner_number ?? '')}`),
    manager_user_id: row.manager_user_id ? String(row.manager_user_id) : null,
    manager_name: row.manager_name ? String(row.manager_name) : null,
    revenue_rub: Number(row.revenue_rub ?? 0),
    place_in_group: Number(row.place_in_group ?? 0),
    place_overall: Number(row.place_overall ?? 0),
  }))
}

export async function getHackathonWednesdayPulse(): Promise<HackathonWednesdayPulseRow | null> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_uk_wednesday_revenue_pulse_payload', {
      p_now: new Date().toISOString(),
    })
  )
  if (error) throw new Error(formatError(error))
  const row = (data?.[0] ?? null) as Record<string, unknown> | null
  if (!row) return null
  return {
    report_date: String(row.report_date ?? ''),
    report_hour_msk: String(row.report_hour_msk ?? ''),
    week_start: String(row.week_start ?? ''),
    week_end: String(row.week_end ?? ''),
    plan_week_rub: Number(row.plan_week_rub ?? 0),
    fact_week_rub: Number(row.fact_week_rub ?? 0),
    today_revenue_rub: Number(row.today_revenue_rub ?? 0),
    remaining_to_plan_rub: Number(row.remaining_to_plan_rub ?? 0),
    over_plan_rub: Number(row.over_plan_rub ?? 0),
    open_studios_count: Number(row.open_studios_count ?? 0),
    remaining_per_open_studio_rub: row.remaining_per_open_studio_rub == null
      ? null
      : Number(row.remaining_per_open_studio_rub),
    listok_last_sync_at: row.listok_last_sync_at ? String(row.listok_last_sync_at) : null,
    top_studios: Array.isArray(row.top_studios)
      ? row.top_studios.map((item) => ({
        rank: Number((item as Record<string, unknown>).rank ?? 0),
        studio_number: String((item as Record<string, unknown>).studio_number ?? ''),
        city_name: String((item as Record<string, unknown>).city_name ?? ''),
        revenue_rub: Number((item as Record<string, unknown>).revenue_rub ?? 0),
      }))
      : [],
    should_send: Boolean(row.should_send),
    skip_reason: row.skip_reason ? String(row.skip_reason) : null,
  }
}
